import * as React from "react";
import { merge, $ } from "glamor";
import { formatMessage } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import { theme } from "styles/theme";
import classNames from "classnames";
import { IconButton, IconMenu as Menu, MenuItem, Paper, Toolbar, ToolbarGroup } from "material-ui";
import MoreVertIcon from "material-ui/svg-icons/navigation/more-vert";
import CloseIcon from "material-ui/svg-icons/navigation/close";
import {
    ASSORTMENT_COPY_ITEMS,
    View,
    CollectionAction,
    Item,
    ValidationModes,
    ASSORTMENT_COPY_ALLOCATIONS,
} from "constants/product-collection";
import { DATATEST } from "constants/integration";
import { amphibiousGetIn } from "helpers/objUtils";
import { List, Map } from "immutable";
import * as copy from "copy-to-clipboard";
import { canUseXLS } from "helpers/product-collection";
import { ChevronRight } from "@material-ui/icons";
import { AllocationBag, BAG_VALUE_PARAMS_SEPARATOR } from "helpers/allocation-dimension";

interface IProps {
    bulkItems: any;
    itemsId: string;
    onBulkItemsClearAll: () => void;
    onOpenDeleteConfirmation: (itemsid, itemIndex) => void;
    gridApi?: any;
    userRole: string;
    items?: List<any>;
    view: View;
    editable: boolean;
    onSnackbarAdd: (params) => void;
    setCopyItemsString: (newValue: string) => void;
    onAllocate: (action: CollectionAction, ids: string[]) => void;
    onValidateItemData: (mode: ValidationModes) => void;
    onMatchSelectedSizesWithSizeRanges: (action: CollectionAction, ids: string[]) => void;
    sizingRangeEnabled: boolean;
    sizingCurveEnabled: boolean;
    allocatingAssortment: boolean;
    findingSizes: boolean;
    sizingAllocationEnabled: boolean;
    pasteAllocation: (row: any, rowIndex: number) => void;
    copiedAllocation: Map<string, any> | null;
    onOpenExportCollectionDialog: () => void;
    onAcceptPackUnits: () => void;
    sizePackEnabled: boolean;
    onOpenExportToMerchantDialog: () => void;
    onSyncInventoryUnits: (action: CollectionAction, ids: string[]) => void;
    syncingInventoryUnits: boolean;
    isCloseoutAssortmentEnabled: boolean;
    exportOptions?: { item: { name: string; active: boolean }; po: { name: string; active: boolean } };
    allocationBagRef?: { allocationBag: AllocationBag };
    fetchBothITemPO: (status: boolean) => void;
}

export class ProductCollectionBulkActions extends React.PureComponent<IProps, any> {
    private TIMEOUT_TO_CLOSE = 50;

    private styles = merge(
        {
            display: "flex",
            height: `${theme.appBar.height}px`,
            position: "fixed",
            top: (theme.appBar.height + 16) * -1 + "px",
            overflow: "hidden",
            left: "0px",
            right: "0px",
            zIndex: 1010,
            transition: "all .2s ease-in-out",
        },
        $(".open", {
            top: "0px",
            overflow: "visible",
        }),
        $(" .toolbar", {
            width: "100%",
        }),
        $(" .closeButton", {
            marginTop: "4px",
            marginLeft: "8px",
            verticalAlign: "middle",
        }),
        $(" .selectedCount", {
            fontSize: "22px",
            verticalAlign: "middle",
            lineHeight: `${theme.appBar.height}px`,
            color: "#676767",
            marginLeft: "20px",
            transition: "all .2s ease-in-out",
        }),
        $(" .selectedCount strong", {
            fontWeight: "bold",
            paddingRight: "2px",
        })
    );

    private menuRef = React.createRef<any>();
    render() {
        const {
            bulkItems,
            onBulkItemsClearAll,
            editable,
            sizingCurveEnabled,
            sizingRangeEnabled,
            allocatingAssortment,
            findingSizes,
            sizingAllocationEnabled,
            copiedAllocation,
            onOpenExportCollectionDialog,
            sizePackEnabled,
            syncingInventoryUnits,
            isCloseoutAssortmentEnabled,
            exportOptions,
            fetchBothITemPO,
        } = this.props;

        return (
            <Paper
                {...this.styles}
                zDepth={2}
                className={classNames({ open: bulkItems.size > 0 })}
                data-test={DATATEST.ASSORTMENTS.BULK_ACTIONS_CONTAINER}
            >
                <Toolbar className="toolbar">
                    <ToolbarGroup firstChild={true}>
                        <IconButton className="closeButton" onClick={onBulkItemsClearAll}>
                            <CloseIcon color={theme.palette.primary2Color} hoverColor={theme.palette.primary1Color} />
                        </IconButton>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <div className="selectedCount">
                            <FormatMessage
                                path="[strong]|count|[strong] Selected"
                                elements={{
                                    strong: <strong />,
                                }}
                                variables={{
                                    count: bulkItems.size,
                                }}
                            />
                        </div>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <Menu
                            iconButtonElement={
                                <IconButton data-test={DATATEST.ASSORTMENTS.BULK_ACTION_BTN}>
                                    <MoreVertIcon />
                                </IconButton>
                            }
                            anchorOrigin={{ horizontal: "right", vertical: "top" }}
                            targetOrigin={{ horizontal: "right", vertical: "top" }}
                            useLayerForClickAway={true}
                            ref={this.menuRef}
                        >
                            {editable && sizingRangeEnabled && (
                                <MenuItem
                                    data-test={DATATEST.ASSORTMENTS.FIND_SIZE_RANGES_BTN}
                                    primaryText={
                                        findingSizes
                                            ? formatMessage("Stop find size ranges")
                                            : formatMessage("Find size ranges")
                                    }
                                    onClick={this.handleFindSizeRanges}
                                />
                            )}
                            {editable && sizingCurveEnabled && (
                                <MenuItem
                                    data-test={DATATEST.ASSORTMENTS.BULK_ALLOCATE_SIZE_CURVE_BTN}
                                    primaryText={
                                        allocatingAssortment
                                            ? formatMessage("Stop Apply Size Curves")
                                            : formatMessage("Apply Size Curves")
                                    }
                                    onClick={this.handleAllocate}
                                />
                            )}
                            {editable && isCloseoutAssortmentEnabled && (
                                <MenuItem
                                    data-test={DATATEST.ASSORTMENTS.BULK_SYNC_INVENTORY_UNITS_BTN}
                                    primaryText={
                                        syncingInventoryUnits
                                            ? formatMessage("Stop Sync Inventory Units")
                                            : formatMessage("Sync Inventory Units")
                                    }
                                    onClick={this.handleSyncInventoryUnitsRows}
                                />
                            )}
                            {editable && sizePackEnabled && (
                                <MenuItem
                                    primaryText={formatMessage("Accept Pack Units")}
                                    onClick={this.handleAcceptPackUnits}
                                />
                            )}
                            {editable && sizingAllocationEnabled && (
                                <MenuItem
                                    disabled={!copiedAllocation}
                                    primaryText={formatMessage("Paste Allocation")}
                                    onClick={this.pasteAllocations}
                                />
                            )}
                            <MenuItem
                                data-test={DATATEST.ASSORTMENTS.BULK_COPY_BTN}
                                primaryText={formatMessage("Copy")}
                                onClick={this.handleCopyItems}
                            />
                            {editable && (
                                <MenuItem
                                    data-test={DATATEST.ASSORTMENTS.BULK_DELETE_BTN}
                                    primaryText={formatMessage("Delete")}
                                    onClick={this.handleOpenDeleteConfirmation}
                                />
                            )}
                            {canUseXLS && (
                                <MenuItem
                                    key="all"
                                    onClick={onOpenExportCollectionDialog}
                                    primaryText={formatMessage("Export")}
                                    data-test={DATATEST.ASSORTMENTS.EXPORT_BTN}
                                />
                            )}

                            {editable && (exportOptions?.item.active || exportOptions?.po.active) && (
                                <MenuItem
                                    data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.MENU_BUTTON}
                                    primaryText={
                                        <div {...this.nestedStyles}>
                                            {exportOptions?.po.active
                                                ? formatMessage("Export |exportNameItem||exportNamePO|", {
                                                      exportNamePO: exportOptions.po.name,
                                                      exportNameItem: exportOptions.item.name,
                                                  })
                                                : formatMessage("Export |exportNameItem|", {
                                                      exportNameItem: exportOptions.item.name,
                                                  })}
                                            <ChevronRight />
                                        </div>
                                    }
                                    menuItems={
                                        <>
                                            <MenuItem
                                                data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.EXPORT_ITEMS}
                                                primaryText={
                                                    exportOptions.item.name && exportOptions.po.name
                                                        ? formatMessage("Export |exportNameItem||exportNamePO| data", {
                                                              exportNamePO: exportOptions.po.name,
                                                              exportNameItem: exportOptions.item.name,
                                                          })
                                                        : formatMessage("Export to |exportNameItem|", {
                                                              exportNameItem: exportOptions.item.name,
                                                          })
                                                }
                                                onClick={this.handleExportToMerchantDialog}
                                            />

                                            <MenuItem
                                                data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.VALIDATE_ITEM_DATA}
                                                primaryText={formatMessage("Validate |exportNameItem| data", {
                                                    exportNameItem: exportOptions.item.name,
                                                })}
                                                onClick={this.handleItemValidation}
                                            />

                                            {exportOptions.po.active && (
                                                <MenuItem
                                                    data-test={DATATEST.ASSORTMENTS.CREATE_ITEMS_PO.VALIDATE_PO_DATA}
                                                    primaryText={formatMessage("Validate |exportNamePO| data", {
                                                        exportNamePO: exportOptions.po.name,
                                                    })}
                                                    onClick={this.handlePOValidation}
                                                />
                                            )}
                                        </>
                                    }
                                />
                            )}
                        </Menu>
                    </ToolbarGroup>
                </Toolbar>
            </Paper>
        );
    }

    private nestedStyles = merge({
        display: "flex",
        alignItems: "center",
    });

    private handleOpenDeleteConfirmation = () => {
        const { itemsId, gridApi, onOpenDeleteConfirmation } = this.props;
        let itemIds = [];
        if (gridApi) {
            itemIds = gridApi.getSelectedNodes().map((node) => {
                return node.data.fields.id.string_value;
            });
            onOpenDeleteConfirmation(itemsId, itemIds);
        }
    };

    private handleItemValidation = () => {
        const { onBulkItemsClearAll, onValidateItemData, fetchBothITemPO } = this.props;
        onBulkItemsClearAll();
        onValidateItemData?.(ValidationModes.ValidateItems);
        fetchBothITemPO(false);
        this.menuRef.current?.close();
    };
    private handlePOValidation = () => {
        const { onBulkItemsClearAll, onValidateItemData, fetchBothITemPO } = this.props;
        onBulkItemsClearAll();
        onValidateItemData?.(ValidationModes.ValidatePOs);
        fetchBothITemPO(false);
        this.menuRef.current?.close();
    };

    private handleExportToMerchantDialog = () => {
        const { onOpenExportToMerchantDialog } = this.props;
        onOpenExportToMerchantDialog();
        this.menuRef.current?.close();
    };

    private pasteAllocations = () => {
        const { gridApi, pasteAllocation, onBulkItemsClearAll } = this.props;

        const selectedItemIds = (gridApi.getSelectedRows() || []).map((item) => item.fields.id.string_value);
        selectedItemIds.forEach((selectedItemId) => {
            const { rowData, rowIndex } = this.getRowById(selectedItemId);

            if (rowData) {
                pasteAllocation(rowData, rowIndex);
            }
        });
        onBulkItemsClearAll();
    };

    private handleCopyItems = () => {
        const { gridApi, view, onSnackbarAdd, setCopyItemsString, onBulkItemsClearAll, allocationBagRef } = this.props;
        if ([View.Grid, View.History].includes(view) && gridApi) {
            onBulkItemsClearAll();
            const selectedItems = gridApi.getSelectedRows() || [];
            const dataFieldsStringArray = JSON.stringify(selectedItems);
            if (allocationBagRef) {
                const selectedItemIds = selectedItems.map((item) => item.fields.id.string_value);
                const allocations = allocationBagRef?.allocationBag
                    .filter((_, key) => {
                        const [itemId] = key.split(BAG_VALUE_PARAMS_SEPARATOR);
                        return selectedItemIds.includes(itemId);
                    })
                    .toJS();
                localStorage.removeItem(ASSORTMENT_COPY_ALLOCATIONS);
                localStorage.setItem(ASSORTMENT_COPY_ALLOCATIONS, JSON.stringify(allocations));
            } else {
                localStorage.removeItem(ASSORTMENT_COPY_ALLOCATIONS);
            }
            localStorage.setItem(ASSORTMENT_COPY_ITEMS, dataFieldsStringArray);
            setCopyItemsString(dataFieldsStringArray);
            copy(dataFieldsStringArray);
            const snackbarMessage =
                selectedItems.length === 1
                    ? formatMessage(`${selectedItems.length} item copied`)
                    : formatMessage(`${selectedItems.length} items copied`);
            onSnackbarAdd({ message: snackbarMessage });
        }
    };
    private handleFindSizeRanges = () => {
        const { gridApi, onBulkItemsClearAll, onMatchSelectedSizesWithSizeRanges, findingSizes } = this.props;
        const action = findingSizes ? CollectionAction.Stop : CollectionAction.Start;
        if (gridApi) {
            const selectedItems = (gridApi.getSelectedRows() || []).map((item) => item.fields.id.string_value);
            onBulkItemsClearAll();
            // allow to close bulk header
            setTimeout(() => onMatchSelectedSizesWithSizeRanges(action, selectedItems), this.TIMEOUT_TO_CLOSE);
        }
    };

    private handleSyncInventoryUnitsRows = () => {
        const { gridApi, onBulkItemsClearAll, onSyncInventoryUnits, syncingInventoryUnits } = this.props;
        const action = syncingInventoryUnits ? CollectionAction.Stop : CollectionAction.Start;

        if (gridApi) {
            const selectedItems = (gridApi.getSelectedRows() || []).map((item) => item.fields.id.string_value);
            onBulkItemsClearAll();
            // allow to close bulk header
            setTimeout(() => onSyncInventoryUnits(action, selectedItems), this.TIMEOUT_TO_CLOSE);
        }
    };

    private handleAllocate = () => {
        const { gridApi, onBulkItemsClearAll, onAllocate, allocatingAssortment } = this.props;
        const action = allocatingAssortment ? CollectionAction.Stop : CollectionAction.Start;
        if (gridApi) {
            const selectedItems = (gridApi.getSelectedRows() || []).map((item) => item.fields.id.string_value);
            onBulkItemsClearAll();
            // allow to close bulk header
            setTimeout(() => onAllocate(action, selectedItems), this.TIMEOUT_TO_CLOSE);
        }
    };

    private handleAcceptPackUnits = () => {
        const { onBulkItemsClearAll, onAcceptPackUnits } = this.props;

        onBulkItemsClearAll();
        onAcceptPackUnits();
    };

    private getRowIndexById = (id) => {
        // gets row index determined by collection not ag-grid
        return this.props.items.findIndex((item) => amphibiousGetIn(item, Item.FIELDS.ID) === id);
    };

    private getRowById = (id) => {
        // gets row index determined by collection not ag-grid
        const rowIndex = this.getRowIndexById(id);
        return { rowData: this.props.items.get(rowIndex), rowIndex };
    };
}
